import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import Header from './header';

const API_BASE = '/api';

const TOTAL_STEPS = 4;
const STEP_LABELS = ['Account', 'Store Basics', 'About', 'Finish'];
const SUPPORTED_CITIES = ['Kelowna', 'Vancouver'];
const CATEGORIES = ['Food', 'Fashion', 'Art', 'Home', 'Technology', 'Health', 'Beauty', 'Sports', 'Books'];
const BUSINESS_TYPES = ['Retail Store', 'Restaurant', 'Service Business', 'Digital Storefront'];

const CreateStorePage = () => {
  const navigate = useNavigate();

  // ── Auth / flow state ──────────────────────────────────────────
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [step, setStep] = useState(1);

  // ── Step 1: account ────────────────────────────────────────────
  const [accountData, setAccountData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });

  // ── Steps 2-4: store ───────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: '',
    city: 'Kelowna',
    businessType: '',
    hasPhysicalLocation: null,
    address: '',
    urlName: '',
    urlNameEdited: false,
    description: '',
    categories: [],
    website: '',
    instagram: '',
    facebook: '',
    whatsapp: '',
    phoneNumber: '',
    profileImage: null,
    businessHours: {
      monday:    { open: '09:00', close: '17:00', isClosed: false },
      tuesday:   { open: '09:00', close: '17:00', isClosed: false },
      wednesday: { open: '09:00', close: '17:00', isClosed: false },
      thursday:  { open: '09:00', close: '17:00', isClosed: false },
      friday:    { open: '09:00', close: '17:00', isClosed: false },
      saturday:  { open: '10:00', close: '15:00', isClosed: false },
      sunday:    { open: '10:00', close: '15:00', isClosed: true  },
    },
  });

  // ── Slug availability ──────────────────────────────────────────
  // 'idle' | 'checking' | 'available' | 'taken' | 'error'
  const [slugStatus, setSlugStatus] = useState('idle');
  const slugDebounceRef = useRef(null);

  // ── UI ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [stepError, setStepError] = useState(null);

  // ── Helpers ────────────────────────────────────────────────────
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 < Date.now() : false;
    } catch {
      return true;
    }
  };

  const generateUrlName = (name) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();

  // ── Initial auth check ─────────────────────────────────────────
  useEffect(() => {
    const checkUserAndStore = async () => {
      const token = localStorage.getItem('token');

      if (!token || isTokenExpired(token)) {
        setInitialCheckDone(true);
        return; // step stays 1
      }

      try {
        const res = await fetch(`${API_BASE}/user/store`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 200) {
          navigate('/dashboard');
          return;
        }

        if (res.status === 404) {
          // Logged in, no store yet — skip account step
          setAuthToken(token);
          setStep(2);
          setInitialCheckDone(true);
          return;
        }

        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
        }
      } catch {
        // Network error — show account step
      }

      setInitialCheckDone(true);
    };

    checkUserAndStore();
  }, [navigate]);

  // ── Slug auto-generation ───────────────────────────────────────
  useEffect(() => {
    if (formData.urlNameEdited) return;
    setFormData((prev) => ({ ...prev, urlName: generateUrlName(formData.name) }));
  }, [formData.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Slug availability check (debounced) ────────────────────────
  useEffect(() => {
    clearTimeout(slugDebounceRef.current);

    if (!formData.urlName || formData.urlName.length < 2) {
      setSlugStatus('idle');
      return;
    }

    setSlugStatus('checking');

    slugDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/stores/check-slug/${formData.urlName}`);
        const data = await res.json();
        setSlugStatus(data.available ? 'available' : 'taken');
      } catch {
        setSlugStatus('error');
      }
    }, 400);

    return () => clearTimeout(slugDebounceRef.current);
  }, [formData.urlName]);

  // ── Step 1 submit: create account ──────────────────────────────
  const handleStep1Submit = async () => {
    setStepError(null);

    const { email, password, confirmPassword } = accountData;

    if (!email || !email.includes('@') || !email.includes('.')) {
      setStepError('Please enter a valid email address.');
      return;
    }
    if (!password || password.length < 6) {
      setStepError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setStepError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data?.error === 'Email already registered') {
          setStepError(
            <>
              An account with this email already exists.{' '}
              <a href="/login" className="alert-link">Sign in instead</a>.
            </>
          );
        } else {
          setStepError(data?.error || 'Registration failed. Please try again.');
        }
        return;
      }

      localStorage.setItem('token', data.token);
      setAuthToken(data.token);
      setStep(2);
    } catch {
      setStepError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 advance: validate store basics ──────────────────────
  const handleStep2Next = () => {
    setStepError(null);

    if (!formData.name.trim()) {
      setStepError('Store name is required.');
      return;
    }
    if (!formData.city) {
      setStepError('Please select a city.');
      return;
    }
    if (formData.hasPhysicalLocation === null) {
      setStepError('Please choose a location type.');
      return;
    }
    if (slugStatus === 'taken') {
      setStepError('This URL is already taken. Edit the store URL below.');
      return;
    }
    if (slugStatus === 'checking') {
      setStepError('Please wait while we check URL availability.');
      return;
    }

    setStep(3);
  };

  // ── Step 3 advance: validate about ────────────────────────────
  const handleStep3Next = () => {
    setStepError(null);
    if (!formData.description.trim()) {
      setStepError('Please add a store description.');
      return;
    }
    setStep(4);
  };

  // ── Final submit ───────────────────────────────────────────────
  const handleSubmit = async () => {
    setStepError(null);
    setLoading(true);

    const token = authToken || localStorage.getItem('token');

    if (!token) {
      setStepError('Your session expired. Please start over.');
      setStep(1);
      setLoading(false);
      return;
    }

    // Pre-flight guard
    if (!formData.name || !formData.description || !formData.city || !formData.urlName) {
      setStepError('Some required fields are missing. Please go back and check.');
      setLoading(false);
      return;
    }

    try {
      const storePayload = {
        name: formData.name,
        description: formData.description,
        city: formData.city,
        url_name: formData.urlName,
        has_physical_location: formData.hasPhysicalLocation || false,
        address: formData.address || '',
        website: formData.website || '',
        contact_email: '',
        phone_number: formData.phoneNumber || '',
        business_hours: formData.businessHours,
        social_media: {
          instagram: formData.instagram || '',
          facebook: formData.facebook || '',
          whatsapp: formData.whatsapp || '',
          twitter: '',
        },
        business_type: formData.businessType || '',
        categories: formData.categories,
      };

      const storeRes = await fetch(`${API_BASE}/stores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(storePayload),
      });

      if (!storeRes.ok) {
        const errData = await storeRes.json();

        if (errData?.error === 'A store with this URL already exists') {
          setStep(2);
          setSlugStatus('taken');
          setFormData((prev) => ({ ...prev, urlName: '', urlNameEdited: false }));
          setStepError('This URL was just taken. Please choose a different store name or URL.');
          setLoading(false);
          return;
        }

        if (storeRes.status === 401 || storeRes.status === 403) {
          localStorage.removeItem('token');
          setStep(1);
          setStepError('Your session expired. Please start over.');
          setLoading(false);
          return;
        }

        throw new Error(errData?.error || 'Failed to create store.');
      }

      const storeData = await storeRes.json();

      // Logo upload (non-fatal)
      if (formData.profileImage) {
        const logoForm = new FormData();
        logoForm.append('logo', formData.profileImage);
        try {
          await fetch(`${API_BASE}/stores/${storeData.id}/logo`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: logoForm,
          });
        } catch (logoErr) {
          console.warn('[CreateStore] Logo upload failed (non-fatal):', logoErr);
        }
      }

      navigate(`/${formData.city.toLowerCase()}/${formData.urlName}`);
    } catch (err) {
      console.error('[CreateStore] Submit error:', err);
      setStepError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // ── Step indicator ─────────────────────────────────────────────
  const StepIndicator = () => {
    const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;
    return (
      <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #eee' }}>
        <div className="d-flex justify-content-between" style={{ marginBottom: '0.75rem' }}>
          {STEP_LABELS.map((label, idx) => {
            const stepNum = idx + 1;
            const done = stepNum < step;
            const active = stepNum === step;
            return (
              <div key={label} className="text-center" style={{ flex: 1 }}>
                <div
                  className="d-flex align-items-center justify-content-center mx-auto"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 4,
                    background: done ? '#10b981' : active ? '#FF385C' : '#f3f4f6',
                    color: done || active ? '#fff' : '#9ca3af',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {done ? <i className="bi bi-check-lg"></i> : stepNum}
                </div>
                <small
                  style={{
                    fontSize: 11,
                    fontWeight: active ? 600 : 400,
                    color: done ? '#10b981' : active ? '#FF385C' : '#9ca3af',
                  }}
                >
                  {label}
                </small>
              </div>
            );
          })}
        </div>
        <div style={{ height: 3, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: '#FF385C',
              borderRadius: 3,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    );
  };

  // ── Slug status badge ──────────────────────────────────────────
  const SlugStatusBadge = () => {
    if (slugStatus === 'idle') return null;
    if (slugStatus === 'checking')
      return (
        <small style={{ color: '#6b7280' }}>
          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Checking…
        </small>
      );
    if (slugStatus === 'available')
      return <small style={{ color: '#10b981' }}><i className="bi bi-check-circle me-1"></i>Available</small>;
    if (slugStatus === 'taken')
      return <small style={{ color: '#ef4444' }}><i className="bi bi-x-circle me-1"></i>Already taken — try a different name</small>;
    return <small style={{ color: '#f59e0b' }}><i className="bi bi-exclamation-circle me-1"></i>Could not check — will verify on submit</small>;
  };

  // ── Step content ───────────────────────────────────────────────
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <h4 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
              Create Your Account
            </h4>
            <p style={{ color: '#6b7280', marginBottom: '1.75rem', fontSize: '0.95rem' }}>
              Step 1 of 4 — takes 30 seconds
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="email" className="csp-label">
                Email address<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text csp-input-icon"><Mail size={16} /></span>
                <input
                  id="email"
                  type="email"
                  className="form-control csp-input"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={accountData.email}
                  onChange={(e) => setAccountData((p) => ({ ...p, email: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="password" className="csp-label">
                Password<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text csp-input-icon"><Lock size={16} /></span>
                <input
                  id="password"
                  type="password"
                  className="form-control csp-input"
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  value={accountData.password}
                  onChange={(e) => setAccountData((p) => ({ ...p, password: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.75rem' }}>
              <label htmlFor="confirmPassword" className="csp-label">
                Confirm password<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text csp-input-icon"><Lock size={16} /></span>
                <input
                  id="confirmPassword"
                  type="password"
                  className="form-control csp-input"
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  value={accountData.confirmPassword}
                  onChange={(e) => setAccountData((p) => ({ ...p, confirmPassword: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <a href="/login" style={{ color: '#6b7280', fontSize: '0.875rem', textDecoration: 'none' }}>
                Already have an account? <span style={{ color: '#FF385C' }}>Sign in</span>
              </a>
              <button
                type="button"
                className="csp-btn-primary"
                onClick={handleStep1Submit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating account…
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h4 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
              Your Store
            </h4>
            <p style={{ color: '#6b7280', marginBottom: '1.75rem', fontSize: '0.95rem' }}>
              Tell us the basics
            </p>

            {/* Business Type */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="csp-label">Business Type</label>
              <div className="row row-cols-2 g-2">
                {BUSINESS_TYPES.map((type) => (
                  <div key={type} className="col">
                    <div
                      className="csp-type-card"
                      style={{
                        border: formData.businessType === type ? '2px solid #FF385C' : '1px solid #e5e7eb',
                        background: formData.businessType === type ? '#fff5f7' : '#fff',
                        borderRadius: 10,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        padding: '0.875rem 0.5rem',
                        textAlign: 'center',
                      }}
                      onClick={() => setFormData((p) => ({ ...p, businessType: type }))}
                    >
                      <i className="bi bi-shop d-block" style={{ fontSize: '1.25rem', marginBottom: 4, color: formData.businessType === type ? '#FF385C' : '#6b7280' }}></i>
                      <small style={{ fontWeight: 500, color: formData.businessType === type ? '#FF385C' : '#374151' }}>{type}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Store Name */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="storeName" className="csp-label">
                Store Name<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <input
                id="storeName"
                type="text"
                className="form-control csp-input"
                placeholder="e.g. Sunrise Bakery"
                value={formData.name}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>

            {/* City */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="city" className="csp-label">
                City<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <select
                id="city"
                className="form-control csp-input"
                value={formData.city}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, city: e.target.value }))
                }
              >
                {SUPPORTED_CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Store URL */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="urlName" className="csp-label">
                Store URL
              </label>
              <div className="input-group">
                <span className="input-group-text csp-input-icon" style={{ fontSize: 13 }}>
                  storetoyou.ca/{formData.city.toLowerCase()}/
                </span>
                <input
                  id="urlName"
                  type="text"
                  className={`form-control csp-input ${slugStatus === 'taken' ? 'is-invalid' : slugStatus === 'available' ? 'is-valid' : ''}`}
                  value={formData.urlName}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, urlName: e.target.value, urlNameEdited: true }))
                  }
                  placeholder="your-store-name"
                />
              </div>
              <div className="d-flex justify-content-between" style={{ marginTop: 6 }}>
                <SlugStatusBadge />
                {formData.urlNameEdited && (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#6b7280', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() =>
                      setFormData((p) => ({ ...p, urlNameEdited: false, urlName: generateUrlName(p.name) }))
                    }
                  >
                    Reset to auto
                  </button>
                )}
              </div>
            </div>

            {/* Location Type */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label className="csp-label">
                Business Location<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="csp-location-btn"
                  style={{
                    flex: 1,
                    background: formData.hasPhysicalLocation === true ? '#FF385C' : '#fff',
                    color: formData.hasPhysicalLocation === true ? '#fff' : '#374151',
                    border: formData.hasPhysicalLocation === true ? '2px solid #FF385C' : '1px solid #d1d5db',
                  }}
                  onClick={() =>
                    setFormData((p) => ({ ...p, hasPhysicalLocation: true }))
                  }
                >
                  <i className="bi bi-geo-alt me-2"></i>Physical Location
                </button>
                <button
                  type="button"
                  className="csp-location-btn"
                  style={{
                    flex: 1,
                    background: formData.hasPhysicalLocation === false ? '#FF385C' : '#fff',
                    color: formData.hasPhysicalLocation === false ? '#fff' : '#374151',
                    border: formData.hasPhysicalLocation === false ? '2px solid #FF385C' : '1px solid #d1d5db',
                  }}
                  onClick={() =>
                    setFormData((p) => ({ ...p, hasPhysicalLocation: false, address: '' }))
                  }
                >
                  <i className="bi bi-globe me-2"></i>Digital Storefront
                </button>
              </div>
            </div>

            {/* Address (conditional) */}
            {formData.hasPhysicalLocation && (
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="address" className="csp-label">Address</label>
                <input
                  id="address"
                  type="text"
                  className="form-control csp-input"
                  placeholder={`123 Main St, ${formData.city}, BC`}
                  value={formData.address}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, address: e.target.value }))
                  }
                />
              </div>
            )}

            <div className="d-flex justify-content-end" style={{ marginTop: '1.75rem' }}>
              <button type="button" className="csp-btn-primary" onClick={handleStep2Next}>
                Continue
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h4 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
              About Your Store
            </h4>
            <p style={{ color: '#6b7280', marginBottom: '1.75rem', fontSize: '0.95rem' }}>
              Help customers understand what you offer
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="description" className="csp-label">
                Description<span style={{ color: '#FF385C' }}>*</span>
              </label>
              <textarea
                id="description"
                className="form-control csp-input"
                rows={4}
                placeholder="Tell customers what makes your business special…"
                value={formData.description}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, description: e.target.value }))
                }
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="csp-label">Categories</label>
              <div className="d-flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className="csp-chip"
                    style={{
                      background: formData.categories.includes(cat) ? '#FF385C' : '#fff',
                      color: formData.categories.includes(cat) ? '#fff' : '#374151',
                      border: formData.categories.includes(cat) ? '1.5px solid #FF385C' : '1px solid #d1d5db',
                    }}
                    onClick={() =>
                      setFormData((p) => ({
                        ...p,
                        categories: p.categories.includes(cat)
                          ? p.categories.filter((c) => c !== cat)
                          : [...p.categories, cat],
                      }))
                    }
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-between" style={{ marginTop: '1.75rem' }}>
              <button type="button" className="csp-btn-back" onClick={() => { setStepError(null); setStep(2); }}>
                Back
              </button>
              <button type="button" className="csp-btn-primary" onClick={handleStep3Next}>
                Continue
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h4 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
              Finish &amp; Launch
            </h4>
            <p style={{ color: '#6b7280', marginBottom: '0.25rem', fontSize: '0.95rem' }}>
              All fields below are optional — you can fill them in later from your dashboard.
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '1.75rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Click <strong>Skip &amp; Launch</strong> to go live right now.
            </p>

            {/* Logo */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="csp-label">Store Logo</label>
              <div style={{ border: '2px dashed #e5e7eb', borderRadius: 12, padding: '1.5rem', textAlign: 'center', background: '#fafafa' }}>
                {formData.profileImage ? (
                  <div>
                    <img
                      src={URL.createObjectURL(formData.profileImage)}
                      alt="Store logo preview"
                      className="img-fluid"
                      style={{ maxHeight: 120, objectFit: 'contain', marginBottom: '0.75rem', borderRadius: 8 }}
                    />
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{formData.profileImage.name}</p>
                    <button
                      type="button"
                      className="csp-btn-back"
                      style={{ fontSize: '0.8rem', padding: '0.375rem 1rem', color: '#ef4444', borderColor: '#fecaca' }}
                      onClick={() => setFormData((p) => ({ ...p, profileImage: null }))}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <i className="bi bi-camera d-block" style={{ fontSize: '2rem', color: '#9ca3af', marginBottom: '0.5rem' }}></i>
                    <input
                      type="file"
                      id="profileImage"
                      className="d-none"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0])
                          setFormData((p) => ({ ...p, profileImage: e.target.files[0] }));
                      }}
                    />
                    <button
                      type="button"
                      className="csp-btn-back"
                      style={{ fontSize: '0.85rem' }}
                      onClick={() => document.getElementById('profileImage').click()}
                    >
                      Upload Logo
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Contact & Social */}
            <div className="row g-3" style={{ marginBottom: '1.25rem' }}>
              <div className="col-md-6">
                <label htmlFor="website" className="csp-label">Website</label>
                <input
                  id="website"
                  type="url"
                  className="form-control csp-input"
                  placeholder="https://yourbusiness.com"
                  value={formData.website}
                  onChange={(e) => setFormData((p) => ({ ...p, website: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="phoneNumber" className="csp-label">Phone</label>
                <input
                  id="phoneNumber"
                  type="tel"
                  className="form-control csp-input"
                  placeholder="(250) 555-1234"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData((p) => ({ ...p, phoneNumber: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="instagram" className="csp-label">Instagram</label>
                <div className="input-group">
                  <span className="input-group-text csp-input-icon">@</span>
                  <input
                    id="instagram"
                    type="text"
                    className="form-control csp-input"
                    placeholder="yourbusiness"
                    value={formData.instagram}
                    onChange={(e) => setFormData((p) => ({ ...p, instagram: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="facebook" className="csp-label">Facebook</label>
                <input
                  id="facebook"
                  type="text"
                  className="form-control csp-input"
                  placeholder="facebook.com/yourbusiness"
                  value={formData.facebook}
                  onChange={(e) => setFormData((p) => ({ ...p, facebook: e.target.value }))}
                />
              </div>
            </div>

            {/* Business Hours */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="csp-label">Business Hours</label>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                {Object.keys(formData.businessHours).map((day, idx) => (
                  <div key={day} style={{ padding: '0.75rem 1rem', borderBottom: idx < 6 ? '1px solid #f3f4f6' : 'none' }}>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: formData.businessHours[day].isClosed ? 0 : 6 }}>
                      <span style={{ textTransform: 'capitalize', fontWeight: 500, fontSize: '0.875rem', color: '#374151' }}>{day}</span>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`${day}-toggle`}
                          checked={!formData.businessHours[day].isClosed}
                          onChange={() =>
                            setFormData((p) => ({
                              ...p,
                              businessHours: {
                                ...p.businessHours,
                                [day]: { ...p.businessHours[day], isClosed: !p.businessHours[day].isClosed },
                              },
                            }))
                          }
                        />
                        <label className="form-check-label" htmlFor={`${day}-toggle`} style={{ fontSize: '0.8rem', color: formData.businessHours[day].isClosed ? '#9ca3af' : '#10b981' }}>
                          {formData.businessHours[day].isClosed ? 'Closed' : 'Open'}
                        </label>
                      </div>
                    </div>
                    {!formData.businessHours[day].isClosed && (
                      <div className="row g-2">
                        <div className="col-6">
                          <input
                            type="time"
                            className="form-control csp-input"
                            style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                            value={formData.businessHours[day].open}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                businessHours: {
                                  ...p.businessHours,
                                  [day]: { ...p.businessHours[day], open: e.target.value },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="time"
                            className="form-control csp-input"
                            style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                            value={formData.businessHours[day].close}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                businessHours: {
                                  ...p.businessHours,
                                  [day]: { ...p.businessHours[day], close: e.target.value },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center" style={{ marginTop: '1.75rem' }}>
              <button type="button" className="csp-btn-back" onClick={() => { setStepError(null); setStep(3); }}>
                Back
              </button>
              <div className="d-flex gap-2">
                <button type="button" className="csp-btn-skip" onClick={handleSubmit} disabled={loading}>
                  Skip &amp; Launch
                </button>
                <button type="button" className="csp-btn-primary" onClick={handleSubmit} disabled={loading}>
                  Launch Store
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Scoped styles ──────────────────────────────────────────────
  const scopedStyles = `
    .csp-page {
      min-height: calc(100vh - 80px);
      background: #dde1e7;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 1.5rem 1rem 2rem;
    }

    .csp-card-wrap {
      max-width: 520px;
      width: 100%;
    }

    .csp-card {
      background: #ffffff;
      border: 1px solid #d0d5dd;
      border-radius: 14px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
      padding: 2rem 2.25rem;
    }

    .csp-card-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 1.75rem;
    }

    .csp-label {
      display: block;
      font-size: 0.875rem;
      font-weight: 600;
      color: #374151;
      margin-bottom: 0.5rem;
    }

    .csp-input {
      border: 1px solid #d1d5db !important;
      border-radius: 8px !important;
      padding: 0.75rem 1rem !important;
      font-size: 0.95rem !important;
      color: #1f2937 !important;
      transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
    }

    .csp-input:focus {
      border-color: #FF385C !important;
      box-shadow: 0 0 0 3px rgba(255, 56, 92, 0.1) !important;
      outline: none !important;
    }

    .csp-input::placeholder {
      color: #9ca3af !important;
    }

    .csp-input-icon {
      background: #f9fafb !important;
      border: 1px solid #d1d5db !important;
      border-right: none !important;
      border-radius: 8px 0 0 8px !important;
      color: #6b7280 !important;
      padding: 0.75rem 0.875rem !important;
    }

    .input-group .csp-input {
      border-left: none !important;
      border-radius: 0 8px 8px 0 !important;
    }

    .csp-btn-primary {
      background: #FF385C;
      color: #fff;
      border: none;
      border-radius: 25px;
      padding: 0.75rem 2rem;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .csp-btn-primary:hover:not(:disabled) {
      background: #e0314f;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(255, 56, 92, 0.3);
    }

    .csp-btn-primary:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .csp-btn-back {
      background: #fff;
      color: #374151;
      border: 1px solid #d1d5db;
      border-radius: 25px;
      padding: 0.75rem 1.5rem;
      font-size: 0.95rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .csp-btn-back:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }

    .csp-btn-skip {
      background: transparent;
      color: #374151;
      border: 1px solid #d1d5db;
      border-radius: 25px;
      padding: 0.75rem 1.5rem;
      font-size: 0.95rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .csp-btn-skip:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }

    .csp-btn-skip:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .csp-chip {
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .csp-chip:hover {
      transform: translateY(-1px);
    }

    .csp-location-btn {
      padding: 0.75rem 1rem;
      border-radius: 10px;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .csp-location-btn:hover {
      transform: translateY(-1px);
    }

    .csp-alert {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 10px;
      padding: 0.75rem 1rem;
      color: #991b1b;
      font-size: 0.9rem;
      margin-bottom: 1.25rem;
    }

    .csp-alert a {
      color: #FF385C;
      font-weight: 600;
    }

    @media (max-width: 600px) {
      .csp-page {
        padding: 0.75rem 0.5rem 1.5rem;
      }

      .csp-card {
        padding: 1.25rem 1rem;
        border-radius: 12px;
      }

      .csp-card-title {
        font-size: 1rem;
        margin-bottom: 1.25rem;
      }
    }

    @media (max-width: 400px) {
      .csp-page {
        padding: 0.5rem 0.375rem 1rem;
      }

      .csp-card {
        padding: 1rem 0.875rem;
        border-radius: 10px;
      }
    }
  `;

  // ── Render ─────────────────────────────────────────────────────
  if (!initialCheckDone) {
    return (
      <>
        <style>{scopedStyles}</style>
        <Header />
        <div className="csp-page">
          <div className="csp-card-wrap">
            <div className="csp-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
              <div className="spinner-border mb-3" role="status" style={{ color: '#FF385C' }}>
                <span className="visually-hidden">Loading…</span>
              </div>
              <p style={{ color: '#6b7280', margin: 0 }}>Checking your account…</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{scopedStyles}</style>
      <Header />
      <div className="csp-page">
        <div className="csp-card-wrap">
          <div className="csp-card">
            <div className="csp-card-title">Create Your Store</div>

            <StepIndicator />

            {stepError && (
              <div className="csp-alert" role="alert">
                {stepError}
              </div>
            )}

            {/* Loading overlay for final submit */}
            {loading && step === 4 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <div className="spinner-border mb-3" role="status" style={{ color: '#FF385C' }}>
                  <span className="visually-hidden">Loading…</span>
                </div>
                <p style={{ color: '#6b7280', margin: 0, fontWeight: 500 }}>Setting up your store…</p>
              </div>
            ) : (
              renderStepContent()
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateStorePage;
